import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calendar,
  Clock,
  Users,
  AlertTriangle,
  CheckCircle,
  Settings,
  Download,
  Upload,
  Play,
  Pause,
  RotateCcw,
  Zap,
  Target,
  Shuffle,
  Eye,
  Edit,
  Trash2,
  Plus,
  ArrowRight,
  ArrowLeft,
  Save,
  FileText,
  BarChart3,
  Layers,
  Timer,
  UserCheck,
  Building,
  Palette,
  Music,
  Award,
  MapPin,
  ChevronDown,
  ChevronUp,
  X,
  Info,
  Lightbulb,
  Wand2
} from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

/**
 * ScheduleCreator - Intelligent competition schedule creator
 * 
 * Features:
 * - Automatic conflict detection (costume changes, dancer overlaps)
 * - Intelligent scheduling algorithm considering group complexity
 * - Drag-and-drop schedule editing
 * - Multi-stage support
 * - Real-time conflict resolution
 * - Export capabilities (PDF, CSV, Live Schedule)
 */
const ScheduleCreator = ({ 
  routines = [], 
  stages = [], 
  onScheduleUpdate,
  onExport,
  eventName = "Competition"
}) => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';

  // Core state
  const [schedule, setSchedule] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedStage, setSelectedStage] = useState(stages[0]?.id || 'main');
  
  // Algorithm settings
  const [settings, setSettings] = useState({
    costumeChangeBuffer: 15, // minutes between routines for same dancers
    groupComplexityMultiplier: 1.5, // extra time for larger groups
    studioBuffer: 5, // minutes between routines from same studio
    ageGroupFlow: true, // keep age groups together when possible
    minimumGap: 3, // minimum minutes between any routines
    maxRoutinesPerHour: 20, // performance capacity limit
    startTime: '09:00', // competition start time
    lunchBreak: { start: '12:00', duration: 60 }, // lunch break
    awards: { frequency: 50, duration: 15 } // awards every N routines for X minutes
  });

  // UI state
  const [activeTab, setActiveTab] = useState('generator');
  const [showSettings, setShowSettings] = useState(false);
  const [showConflicts, setShowConflicts] = useState(false);
  const [editingRoutine, setEditingRoutine] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);

  // Statistics
  const [stats, setStats] = useState({
    totalRoutines: 0,
    estimatedDuration: 0,
    conflictCount: 0,
    stageUtilization: {},
    peakHours: []
  });

  /**
   * Intelligent Schedule Generation Algorithm
   */
  const generateSchedule = useCallback(async () => {
    setIsGenerating(true);
    
    try {
      // Step 1: Analyze routines and create dancer mapping
      const analysis = analyzeRoutines(routines);
      
      // Step 2: Create initial schedule with basic ordering
      let initialSchedule = createInitialSchedule(routines, analysis);
      
      // Step 3: Apply intelligent scheduling algorithm
      const optimizedSchedule = await optimizeSchedule(initialSchedule, analysis);
      
      // Step 4: Assign stages and times
      const finalSchedule = assignStagesAndTimes(optimizedSchedule, stages);
      
      // Step 5: Detect conflicts
      const detectedConflicts = detectConflicts(finalSchedule, analysis);
      
      setSchedule(finalSchedule);
      setConflicts(detectedConflicts);
      updateStatistics(finalSchedule, detectedConflicts);
      
    } catch (error) {
      console.error('Schedule generation failed:', error);
    } finally {
      setIsGenerating(false);
    }
  }, [routines, stages, settings]);

  /**
   * Analyze routines to extract dancer information and complexity
   */
  const analyzeRoutines = useCallback((routines) => {
    const analysis = {
      dancerMap: new Map(), // dancer -> routine IDs
      studioMap: new Map(), // studio -> routine IDs
      complexityScores: new Map(), // routine ID -> complexity score
      ageGroups: new Map(), // age group -> routine IDs
      categories: new Map() // category -> routine IDs
    };

    routines.forEach(routine => {
      const routineId = routine.routineNumber || routine.id;
      const dancerCount = parseInt(routine['# of Dancers'] || routine.dancersCount || 1);
      const studio = routine['Studio Code'] || routine.studioCode || '';
      const ageGroup = routine['Age Group'] || routine.ageGroup || '';
      const category = routine.Category || routine.category || '';
      const performerName = routine['Performer Name'] || routine.performerName || '';

      // Calculate complexity score based on multiple factors
      let complexityScore = 1;
      
      // Group size complexity (exponential growth)
      if (dancerCount > 1) {
        complexityScore += Math.pow(dancerCount, 1.3);
      }
      
      // Style complexity
      const style = (routine.Type || routine.type || '').toLowerCase();
      if (['contemporary', 'lyrical'].includes(style)) complexityScore += 0.5;
      if (['tap', 'musical theatre'].includes(style)) complexityScore += 0.3;
      
      // Age group complexity (younger = more complex)
      if (ageGroup.toLowerCase().includes('mini')) complexityScore += 0.8;
      if (ageGroup.toLowerCase().includes('junior')) complexityScore += 0.5;
      
      analysis.complexityScores.set(routineId, complexityScore);

      // Map dancers (for solo routines, use performer name)
      if (dancerCount === 1 && performerName) {
        if (!analysis.dancerMap.has(performerName)) {
          analysis.dancerMap.set(performerName, []);
        }
        analysis.dancerMap.get(performerName).push(routineId);
      } else if (dancerCount > 1) {
        // For group routines, create a group identifier
        const groupId = `${studio}-${routine.name || routine.title}-group`;
        if (!analysis.dancerMap.has(groupId)) {
          analysis.dancerMap.set(groupId, []);
        }
        analysis.dancerMap.get(groupId).push(routineId);
      }

      // Map studios
      if (studio) {
        if (!analysis.studioMap.has(studio)) {
          analysis.studioMap.set(studio, []);
        }
        analysis.studioMap.get(studio).push(routineId);
      }

      // Map age groups
      if (ageGroup) {
        if (!analysis.ageGroups.has(ageGroup)) {
          analysis.ageGroups.set(ageGroup, []);
        }
        analysis.ageGroups.get(ageGroup).push(routineId);
      }

      // Map categories
      if (category) {
        if (!analysis.categories.has(category)) {
          analysis.categories.set(category, []);
        }
        analysis.categories.get(category).push(routineId);
      }
    });

    return analysis;
  }, []);

  /**
   * Create initial schedule with basic ordering
   */
  const createInitialSchedule = useCallback((routines, analysis) => {
    // Sort routines by multiple criteria
    return [...routines].sort((a, b) => {
      const aId = a.routineNumber || a.id;
      const bId = b.routineNumber || b.id;
      
      // Primary sort: Age group (Mini -> Junior -> Teen -> Senior)
      const ageOrder = { 'mini': 1, 'junior': 2, 'teen': 3, 'senior': 4 };
      const aAge = (a['Age Group'] || a.ageGroup || '').toLowerCase();
      const bAge = (b['Age Group'] || b.ageGroup || '').toLowerCase();
      
      const aAgeScore = Object.keys(ageOrder).find(key => aAge.includes(key));
      const bAgeScore = Object.keys(ageOrder).find(key => bAge.includes(key));
      
      if (aAgeScore && bAgeScore && aAgeScore !== bAgeScore) {
        return ageOrder[aAgeScore] - ageOrder[bAgeScore];
      }

      // Secondary sort: Category (Solo -> Duo/Trio -> Group)
      const categoryOrder = { 'solo': 1, 'duo': 2, 'trio': 2, 'group': 3 };
      const aCategory = (a.Division || a.division || '').toLowerCase();
      const bCategory = (b.Division || b.division || '').toLowerCase();
      
      const aCatScore = Object.keys(categoryOrder).find(key => aCategory.includes(key));
      const bCatScore = Object.keys(categoryOrder).find(key => bCategory.includes(key));
      
      if (aCatScore && bCatScore && aCatScore !== bCatScore) {
        return categoryOrder[aCatScore] - categoryOrder[bCatScore];
      }

      // Tertiary sort: Complexity (simple -> complex)
      const aComplexity = analysis.complexityScores.get(aId) || 1;
      const bComplexity = analysis.complexityScores.get(bId) || 1;
      
      if (Math.abs(aComplexity - bComplexity) > 0.5) {
        return aComplexity - bComplexity;
      }

      // Final sort: Routine number
      const aNum = parseInt(aId) || 0;
      const bNum = parseInt(bId) || 0;
      return aNum - bNum;
    });
  }, []);

  /**
   * Optimize schedule using intelligent algorithm
   */
  const optimizeSchedule = useCallback(async (initialSchedule, analysis) => {
    let optimizedSchedule = [...initialSchedule];
    let improvements = 0;
    const maxIterations = 100;

    for (let iteration = 0; iteration < maxIterations; iteration++) {
      let improved = false;

      // Try swapping adjacent routines to reduce conflicts
      for (let i = 0; i < optimizedSchedule.length - 1; i++) {
        const currentConflicts = calculateConflictScore(optimizedSchedule, i, i + 1, analysis);
        
        // Try swap
        const swapped = [...optimizedSchedule];
        [swapped[i], swapped[i + 1]] = [swapped[i + 1], swapped[i]];
        const swappedConflicts = calculateConflictScore(swapped, i, i + 1, analysis);

        if (swappedConflicts < currentConflicts) {
          optimizedSchedule = swapped;
          improved = true;
          improvements++;
        }
      }

      // Try moving routines with high conflict scores
      for (let i = 0; i < optimizedSchedule.length; i++) {
        const routine = optimizedSchedule[i];
        const routineId = routine.routineNumber || routine.id;
        
        // Find best position for this routine
        let bestPosition = i;
        let bestScore = calculateRoutineConflictScore(optimizedSchedule, i, analysis);

        for (let j = 0; j < optimizedSchedule.length; j++) {
          if (i === j) continue;
          
          const moved = [...optimizedSchedule];
          moved.splice(i, 1);
          moved.splice(j, 0, routine);
          
          const newScore = calculateRoutineConflictScore(moved, j, analysis);
          if (newScore < bestScore) {
            bestScore = newScore;
            bestPosition = j;
          }
        }

        if (bestPosition !== i) {
          const moved = [...optimizedSchedule];
          moved.splice(i, 1);
          moved.splice(bestPosition, 0, routine);
          optimizedSchedule = moved;
          improved = true;
          improvements++;
        }
      }

      if (!improved) break;
    }

    console.log(`Schedule optimization completed: ${improvements} improvements over ${maxIterations} iterations`);
    return optimizedSchedule;
  }, [settings]);

  /**
   * Calculate conflict score between two adjacent routines
   */
  const calculateConflictScore = useCallback((schedule, index1, index2, analysis) => {
    if (index1 >= schedule.length || index2 >= schedule.length) return 0;
    
    const routine1 = schedule[index1];
    const routine2 = schedule[index2];
    const id1 = routine1.routineNumber || routine1.id;
    const id2 = routine2.routineNumber || routine2.id;
    
    let conflictScore = 0;

    // Check dancer conflicts
    for (const [dancer, routineIds] of analysis.dancerMap.entries()) {
      if (routineIds.includes(id1) && routineIds.includes(id2)) {
        conflictScore += 10; // High penalty for same dancer in adjacent routines
      }
    }

    // Check studio conflicts
    const studio1 = routine1['Studio Code'] || routine1.studioCode;
    const studio2 = routine2['Studio Code'] || routine2.studioCode;
    if (studio1 && studio2 && studio1 === studio2) {
      conflictScore += 3; // Medium penalty for same studio
    }

    // Check complexity transitions
    const complexity1 = analysis.complexityScores.get(id1) || 1;
    const complexity2 = analysis.complexityScores.get(id2) || 1;
    if (complexity1 > 3 && complexity2 > 3) {
      conflictScore += 2; // Penalty for consecutive complex routines
    }

    return conflictScore;
  }, []);

  /**
   * Calculate conflict score for a single routine in its position
   */
  const calculateRoutineConflictScore = useCallback((schedule, index, analysis) => {
    let score = 0;
    
    if (index > 0) {
      score += calculateConflictScore(schedule, index - 1, index, analysis);
    }
    
    if (index < schedule.length - 1) {
      score += calculateConflictScore(schedule, index, index + 1, analysis);
    }
    
    return score;
  }, [calculateConflictScore]);

  /**
   * Assign stages and times to the optimized schedule
   */
  const assignStagesAndTimes = useCallback((schedule, stages) => {
    const stageSchedules = stages.reduce((acc, stage) => {
      acc[stage.id] = [];
      return acc;
    }, {});

    let currentTime = parseTime(settings.startTime);
    let currentStageIndex = 0;
    
    return schedule.map((routine, index) => {
      const routineId = routine.routineNumber || routine.id;
      const complexity = 1; // Will be calculated from analysis
      const dancerCount = parseInt(routine['# of Dancers'] || routine.dancersCount || 1);
      
      // Calculate routine duration
      let duration = 4; // Base duration in minutes
      if (routine['Song Length'] || routine.songLength) {
        const songLength = parseTimeToMinutes(routine['Song Length'] || routine.songLength);
        duration = Math.max(songLength + 1, 3); // Song + 1 minute buffer, minimum 3 minutes
      }
      
      // Add complexity buffer
      if (dancerCount > 5) duration += 2;
      if (dancerCount > 10) duration += 3;
      
      // Assign stage (round-robin for now)
      const assignedStage = stages[currentStageIndex % stages.length];
      currentStageIndex++;
      
      // Calculate start time
      const startTime = formatTime(currentTime);
      const endTime = formatTime(currentTime + duration);
      
      currentTime += duration + settings.minimumGap;
      
      // Add breaks
      if (index > 0 && index % settings.awards.frequency === 0) {
        currentTime += settings.awards.duration;
      }
      
      // Add lunch break
      if (currentTime >= parseTime(settings.lunchBreak.start) && 
          currentTime < parseTime(settings.lunchBreak.start) + settings.lunchBreak.duration) {
        currentTime = parseTime(settings.lunchBreak.start) + settings.lunchBreak.duration;
      }

      return {
        ...routine,
        scheduleId: `schedule-${index}`,
        stageId: assignedStage.id,
        stageName: assignedStage.name,
        startTime,
        endTime,
        duration,
        estimatedDuration: duration,
        order: index + 1
      };
    });
  }, [settings]);

  /**
   * Detect conflicts in the final schedule
   */
  const detectConflicts = useCallback((schedule, analysis) => {
    const conflicts = [];
    
    // Check for dancer conflicts (same dancer in overlapping time slots)
    for (const [dancer, routineIds] of analysis.dancerMap.entries()) {
      const dancerRoutines = schedule.filter(item => 
        routineIds.includes(item.routineNumber || item.id)
      ).sort((a, b) => parseTime(a.startTime) - parseTime(b.startTime));
      
      for (let i = 0; i < dancerRoutines.length - 1; i++) {
        const current = dancerRoutines[i];
        const next = dancerRoutines[i + 1];
        
        const currentEnd = parseTime(current.endTime);
        const nextStart = parseTime(next.startTime);
        const gap = nextStart - currentEnd;
        
        if (gap < settings.costumeChangeBuffer) {
          conflicts.push({
            type: 'costume_change',
            severity: gap < 5 ? 'high' : 'medium',
            message: `Insufficient costume change time for ${dancer}`,
            routines: [current, next],
            gap: gap,
            required: settings.costumeChangeBuffer
          });
        }
      }
    }
    
    // Check for studio conflicts
    for (const [studio, routineIds] of analysis.studioMap.entries()) {
      const studioRoutines = schedule.filter(item => 
        routineIds.includes(item.routineNumber || item.id)
      ).sort((a, b) => parseTime(a.startTime) - parseTime(b.startTime));
      
      for (let i = 0; i < studioRoutines.length - 1; i++) {
        const current = studioRoutines[i];
        const next = studioRoutines[i + 1];
        
        const currentEnd = parseTime(current.endTime);
        const nextStart = parseTime(next.startTime);
        const gap = nextStart - currentEnd;
        
        if (gap < settings.studioBuffer) {
          conflicts.push({
            type: 'studio_conflict',
            severity: 'low',
            message: `Short gap between ${studio} routines`,
            routines: [current, next],
            gap: gap,
            required: settings.studioBuffer
          });
        }
      }
    }
    
    return conflicts;
  }, [settings]);

  /**
   * Update statistics based on current schedule
   */
  const updateStatistics = useCallback((schedule, conflicts) => {
    const stageUtilization = {};
    const hourlyDistribution = {};
    
    schedule.forEach(routine => {
      // Stage utilization
      if (!stageUtilization[routine.stageId]) {
        stageUtilization[routine.stageId] = 0;
      }
      stageUtilization[routine.stageId] += routine.duration;
      
      // Hourly distribution
      const hour = Math.floor(parseTime(routine.startTime) / 60);
      if (!hourlyDistribution[hour]) {
        hourlyDistribution[hour] = 0;
      }
      hourlyDistribution[hour]++;
    });
    
    const totalDuration = schedule.length > 0 
      ? parseTime(schedule[schedule.length - 1].endTime) - parseTime(schedule[0].startTime)
      : 0;
    
    setStats({
      totalRoutines: schedule.length,
      estimatedDuration: totalDuration,
      conflictCount: conflicts.length,
      stageUtilization,
      peakHours: Object.entries(hourlyDistribution)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 3)
        .map(([hour, count]) => ({ hour: parseInt(hour), count }))
    });
  }, []);

  // Utility functions
  const parseTime = (timeString) => {
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const formatTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };

  const parseTimeToMinutes = (timeString) => {
    if (!timeString) return 3;
    const parts = timeString.split(':');
    if (parts.length === 2) {
      return parseInt(parts[0]) + Math.ceil(parseInt(parts[1]) / 60);
    }
    return 3;
  };

  /**
   * Handle drag and drop reordering
   */
  const handleDragEnd = useCallback((result) => {
    if (!result.destination) return;

    const items = Array.from(schedule);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Recalculate times and detect conflicts
    const updatedSchedule = assignStagesAndTimes(items, stages);
    const analysis = analyzeRoutines(items);
    const newConflicts = detectConflicts(updatedSchedule, analysis);
    
    setSchedule(updatedSchedule);
    setConflicts(newConflicts);
    updateStatistics(updatedSchedule, newConflicts);
  }, [schedule, stages, assignStagesAndTimes, analyzeRoutines, detectConflicts, updateStatistics]);

  // Initialize on mount
  useEffect(() => {
    if (routines.length > 0 && stages.length > 0) {
      generateSchedule();
    }
  }, [routines, stages, generateSchedule]);

  return (
    <div className={`schedule-creator h-full flex flex-col ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Header */}
      <div className={`flex items-center justify-between p-6 border-b ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
        <div className="flex items-center gap-4">
          <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-purple-900/30' : 'bg-purple-100'}`}>
            <Wand2 className={`w-6 h-6 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
          </div>
          <div>
            <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Schedule Creator
            </h1>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Intelligent competition scheduling with conflict detection
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowSettings(!showSettings)}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-all ${
              isDarkMode 
                ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' 
                : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
            }`}
          >
            <Settings className="w-4 h-4" />
            Settings
          </motion.button>
          
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={generateSchedule}
            disabled={isGenerating}
            className={`px-6 py-2 rounded-lg flex items-center gap-2 font-semibold transition-all ${
              isGenerating
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600'
            } text-white`}
          >
            {isGenerating ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Zap className="w-4 h-4" />
                Generate Schedule
              </>
            )}
          </motion.button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar - Statistics & Controls */}
        <div className={`w-80 border-r ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'} overflow-y-auto`}>
          <div className="p-6 space-y-6">
            {/* Statistics */}
            <div>
              <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Schedule Statistics
              </h3>
              <div className="space-y-3">
                <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <Music className="w-4 h-4 text-blue-500" />
                    <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Total Routines
                    </span>
                  </div>
                  <div className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {stats.totalRoutines}
                  </div>
                </div>
                
                <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <Clock className="w-4 h-4 text-green-500" />
                    <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Duration
                    </span>
                  </div>
                  <div className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {Math.floor(stats.estimatedDuration / 60)}h {stats.estimatedDuration % 60}m
                  </div>
                </div>
                
                <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <AlertTriangle className={`w-4 h-4 ${stats.conflictCount > 0 ? 'text-red-500' : 'text-green-500'}`} />
                    <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Conflicts
                    </span>
                  </div>
                  <div className={`text-2xl font-bold ${stats.conflictCount > 0 ? 'text-red-500' : 'text-green-500'}`}>
                    {stats.conflictCount}
                  </div>
                </div>
              </div>
            </div>

            {/* Conflicts Panel */}
            {conflicts.length > 0 && (
              <div>
                <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Detected Conflicts
                </h3>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {conflicts.map((conflict, index) => (
                    <div 
                      key={index}
                      className={`p-3 rounded-lg border ${
                        conflict.severity === 'high' 
                          ? 'border-red-400 bg-red-50 dark:bg-red-900/20'
                          : conflict.severity === 'medium'
                          ? 'border-yellow-400 bg-yellow-50 dark:bg-yellow-900/20'
                          : 'border-blue-400 bg-blue-50 dark:bg-blue-900/20'
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <AlertTriangle className={`w-4 h-4 mt-0.5 ${
                          conflict.severity === 'high' ? 'text-red-500' :
                          conflict.severity === 'medium' ? 'text-yellow-500' : 'text-blue-500'
                        }`} />
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium ${
                            conflict.severity === 'high' ? 'text-red-800 dark:text-red-200' :
                            conflict.severity === 'medium' ? 'text-yellow-800 dark:text-yellow-200' :
                            'text-blue-800 dark:text-blue-200'
                          }`}>
                            {conflict.message}
                          </p>
                          <p className={`text-xs mt-1 ${
                            conflict.severity === 'high' ? 'text-red-600 dark:text-red-300' :
                            conflict.severity === 'medium' ? 'text-yellow-600 dark:text-yellow-300' :
                            'text-blue-600 dark:text-blue-300'
                          }`}>
                            Gap: {conflict.gap}min (Need: {conflict.required}min)
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Stage Utilization */}
            <div>
              <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Stage Utilization
              </h3>
              <div className="space-y-2">
                {Object.entries(stats.stageUtilization).map(([stageId, duration]) => {
                  const stage = stages.find(s => s.id === stageId);
                  const percentage = (duration / stats.estimatedDuration) * 100;
                  
                  return (
                    <div key={stageId} className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                          {stage?.name || stageId}
                        </span>
                        <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                          {Math.round(percentage)}%
                        </span>
                      </div>
                      <div className={`h-2 rounded-full ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                        <div 
                          className="h-full rounded-full bg-gradient-to-r from-purple-500 to-pink-500"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Main Schedule View */}
        <div className="flex-1 overflow-hidden">
          {schedule.length === 0 ? (
            <div className="h-full flex items-center justify-center">
              <div className="text-center">
                <Lightbulb className={`w-16 h-16 mx-auto mb-4 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`} />
                <h3 className={`text-xl font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Ready to Create Schedule
                </h3>
                <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-600'}`}>
                  Click "Generate Schedule" to create an optimized competition schedule
                </p>
              </div>
            </div>
          ) : (
            <div className="h-full overflow-y-auto p-6">
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="schedule">
                  {(provided) => (
                    <div
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      className="space-y-2"
                    >
                      {schedule.map((routine, index) => (
                        <Draggable 
                          key={routine.scheduleId} 
                          draggableId={routine.scheduleId} 
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              className={`p-4 rounded-lg border transition-all ${
                                snapshot.isDragging
                                  ? 'shadow-lg scale-105'
                                  : 'shadow-sm hover:shadow-md'
                              } ${
                                isDarkMode 
                                  ? 'bg-gray-800 border-gray-700' 
                                  : 'bg-white border-gray-200'
                              }`}
                            >
                              <div className="flex items-center gap-4">
                                <div className="flex-shrink-0">
                                  <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold ${
                                    isDarkMode ? 'bg-purple-900/30 text-purple-400' : 'bg-purple-100 text-purple-600'
                                  }`}>
                                    {routine.routineNumber}
                                  </div>
                                </div>
                                
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <h4 className={`font-semibold truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                      {routine.name || routine['Routine Name']}
                                    </h4>
                                    {routine['Performer Name'] && (
                                      <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                        • {routine['Performer Name']}
                                      </span>
                                    )}
                                  </div>
                                  
                                  <div className="flex items-center gap-4 text-sm">
                                    <div className="flex items-center gap-1">
                                      <Clock className="w-3 h-3" />
                                      <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                                        {routine.startTime} - {routine.endTime}
                                      </span>
                                    </div>
                                    
                                    <div className="flex items-center gap-1">
                                      <MapPin className="w-3 h-3" />
                                      <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                                        {routine.stageName}
                                      </span>
                                    </div>
                                    
                                    <div className="flex items-center gap-1">
                                      <Users className="w-3 h-3" />
                                      <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                                        {routine['# of Dancers'] || 1} dancers
                                      </span>
                                    </div>
                                  </div>
                                  
                                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                                    {/* Studio Code Tag */}
                                    {(routine['Studio Code'] || routine.studioCode) && (
                                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                        isDarkMode ? 'bg-orange-900/30 text-orange-400' : 'bg-orange-100 text-orange-800'
                                      }`}>
                                        {routine['Studio Code'] || routine.studioCode}
                                      </span>
                                    )}
                                    
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                      isDarkMode ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-100 text-blue-800'
                                    }`}>
                                      {routine['Age Group'] || routine.ageGroup}
                                    </span>
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                      isDarkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-800'
                                    }`}>
                                      {routine.Type || routine.type}
                                    </span>
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                      isDarkMode ? 'bg-purple-900/30 text-purple-400' : 'bg-purple-100 text-purple-800'
                                    }`}>
                                      {routine.Division || routine.division}
                                    </span>
                                  </div>
                                </div>
                                
                                <div className="flex-shrink-0 text-right">
                                  <div className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                    #{routine.order}
                                  </div>
                                  <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                    {routine.duration}min
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </div>
          )}
        </div>
      </div>

      {/* Settings Modal */}
      <AnimatePresence>
        {showSettings && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowSettings(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className={`w-full max-w-2xl rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} p-6`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Schedule Settings
                </h2>
                <button
                  onClick={() => setShowSettings(false)}
                  className={`p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Costume Change Buffer (minutes)
                  </label>
                  <input
                    type="number"
                    value={settings.costumeChangeBuffer}
                    onChange={(e) => setSettings(prev => ({ ...prev, costumeChangeBuffer: parseInt(e.target.value) }))}
                    className={`w-full px-3 py-2 rounded-lg border ${
                      isDarkMode 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  />
                </div>
                
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Studio Buffer (minutes)
                  </label>
                  <input
                    type="number"
                    value={settings.studioBuffer}
                    onChange={(e) => setSettings(prev => ({ ...prev, studioBuffer: parseInt(e.target.value) }))}
                    className={`w-full px-3 py-2 rounded-lg border ${
                      isDarkMode 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  />
                </div>
                
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Start Time
                  </label>
                  <input
                    type="time"
                    value={settings.startTime}
                    onChange={(e) => setSettings(prev => ({ ...prev, startTime: e.target.value }))}
                    className={`w-full px-3 py-2 rounded-lg border ${
                      isDarkMode 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  />
                </div>
                
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Minimum Gap (minutes)
                  </label>
                  <input
                    type="number"
                    value={settings.minimumGap}
                    onChange={(e) => setSettings(prev => ({ ...prev, minimumGap: parseInt(e.target.value) }))}
                    className={`w-full px-3 py-2 rounded-lg border ${
                      isDarkMode 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  />
                </div>
              </div>
              
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowSettings(false)}
                  className={`px-4 py-2 rounded-lg ${
                    isDarkMode 
                      ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' 
                      : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowSettings(false);
                    generateSchedule();
                  }}
                  className="px-4 py-2 rounded-lg bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white"
                >
                  Apply & Regenerate
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ScheduleCreator;
